import { Building2, Download, EyeOff, Mail, Search, Shield, ShieldCheck, ShieldOff, UserPlus } from "lucide-react";
import { Button } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { PeopleFilters } from "./PeopleTable";
import { FilterSelectChip } from "./shared/toolbar/FilterSelectChip";
import { ToggleChip } from "./shared/toolbar/ToggleChip";
import { ClearFilters } from "./shared/toolbar/ClearFilters";

interface PeopleToolbarProps {
  filters: PeopleFilters;
  onFilterChange: <K extends keyof PeopleFilters>(key: K, value: PeopleFilters[K]) => void;
  selectedCount: number;
  onClearSelection: () => void;
  onInvite: () => void;
  onBulkAssignRole?: () => void;
  onBulkExport?: () => void;
  orgs: Array<{ id: string; name: string }>;
  roles: string[];
  canInvite?: boolean;
}

export function PeopleToolbar({
  filters, onFilterChange, selectedCount, onClearSelection, onInvite,
  onBulkAssignRole, onBulkExport,
  orgs, roles, canInvite = true,
}: PeopleToolbarProps) {
  const { t } = useTranslation();
  const tb = (key: string, opts?: Record<string, unknown>) => t(`iam.dashboard.people.toolbar.${key}`, opts);

  const STATUS_OPTIONS = [
    { value: "ACTIVE",      label: tb("statusActive")    },
    { value: "PENDING",     label: tb("statusInvited")   },
    { value: "DEACTIVATED", label: tb("statusSuspended") },
  ];

  const activeFilterCount = [
    filters.orgId, filters.role, filters.status, filters.noRole, filters.neverSignedIn, filters.hasPendingInvite,
  ].filter(Boolean).length;
  const activeOrgName = orgs.find((o) => o.id === filters.orgId)?.name;
  const statusLabel = STATUS_OPTIONS.find((s) => s.value === filters.status)?.label;

  const clearAll = () => {
    onFilterChange("orgId", null);
    onFilterChange("role", null);
    onFilterChange("status", null);
    onFilterChange("noRole", false);
    onFilterChange("neverSignedIn", false);
    onFilterChange("hasPendingInvite", false);
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 py-3">
      {/* Search — flex-1 on mobile so it fills the row next to the invite button; fixed-width on sm+ */}
      <label className="relative order-1 flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40 sm:w-56 sm:flex-none">
        <Search className="h-3.5 w-3.5 shrink-0" />
        <input
          type="search"
          placeholder={tb("searchPlaceholder")}
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
        />
        <kbd className="hidden rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:block">
          ⌘K
        </kbd>
      </label>

      {/* Chips / bulk actions — order-3 on mobile (wraps to its own full-width row below search+invite), order-2 on sm+ (sits in the middle) */}
      <div className="order-3 w-full sm:order-2 sm:w-auto sm:flex-1">
        {selectedCount > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <span className="text-sm font-medium text-foreground">
              {tb("selected", { count: selectedCount })}
            </span>
            <div className="mx-1 h-4 w-px bg-border" />
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={onBulkAssignRole}>
              <ShieldCheck className="h-3 w-3" /> {tb("assignRole")}
            </Button>
            <div className="h-4 w-px bg-border" />
            <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={onBulkExport}>
              <Download className="h-3 w-3" /> {tb("export")}
            </Button>
            <div className="h-4 w-px bg-border" />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={onClearSelection}
            >
              {tb("clearSelection")}
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Organization */}
            <FilterSelectChip
              icon={<Building2 className="h-3 w-3 shrink-0" />}
              label={activeOrgName ?? tb("filterOrg")}
              active={!!filters.orgId}
            >
              <select
                value={filters.orgId ?? ""}
                onChange={(e) => onFilterChange("orgId", e.target.value || null)}
                className="absolute inset-0 cursor-pointer opacity-0"
              >
                <option value="">{tb("filterOrgAll")}</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </FilterSelectChip>

            {/* Role */}
            <FilterSelectChip
              icon={<Shield className="h-3 w-3 shrink-0" />}
              label={filters.role ?? tb("filterRole")}
              active={!!filters.role}
            >
              <select
                value={filters.role ?? ""}
                onChange={(e) => onFilterChange("role", e.target.value || null)}
                className="absolute inset-0 cursor-pointer opacity-0"
              >
                <option value="">{tb("filterRoleAny")}</option>
                {roles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </FilterSelectChip>

            {/* Status */}
            <FilterSelectChip
              icon={<span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
              label={statusLabel ?? tb("filterStatus")}
              active={!!filters.status}
            >
              <select
                value={filters.status ?? ""}
                onChange={(e) => onFilterChange("status", e.target.value || null)}
                className="absolute inset-0 cursor-pointer opacity-0"
              >
                <option value="">{tb("filterStatusAny")}</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </FilterSelectChip>

            {/* No role toggle */}
            <ToggleChip
              icon={<ShieldOff className="h-3 w-3 shrink-0" />}
              label={tb("filterNoRole")}
              active={filters.noRole}
              onClick={() => onFilterChange("noRole", !filters.noRole)}
            />

            {/* Never signed in toggle */}
            <ToggleChip
              icon={<EyeOff className="h-3 w-3 shrink-0" />}
              label={tb("filterNeverSignedIn")}
              active={filters.neverSignedIn}
              onClick={() => onFilterChange("neverSignedIn", !filters.neverSignedIn)}
            />

            {/* Has pending invite toggle */}
            <ToggleChip
              icon={<Mail className="h-3 w-3 shrink-0" />}
              label={tb("filterHasPendingInvite")}
              active={filters.hasPendingInvite}
              onClick={() => onFilterChange("hasPendingInvite", !filters.hasPendingInvite)}
            />

            {activeFilterCount > 0 && <ClearFilters label={tb("clearFilters")} onClick={clearAll} />}
          </div>
        )}
      </div>

      {/* Primary action — order-2 on mobile (stays on same row as search); order-3 on sm+ (at the end) */}
      {canInvite && (
        <div className="order-2 shrink-0 sm:order-3">
          <Button variant="default" size="sm" className="gap-1.5" onClick={onInvite}>
            <UserPlus className="h-3.5 w-3.5" />
            {tb("invite")}
          </Button>
        </div>
      )}
    </div>
  );
}
